import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowRight, ShieldCheck, Brain, Compass, Sparkles, ChevronDown, Anchor, Award, Camera, Code } from "lucide-react";
import PricingSection from "@/components/PricingSection";
import { TOOLS, HERO_DESKTOP, HERO_MOBILE, FOUNDER_IMG, SCENIC_IMG } from "@/lib/toolRegistry";
import { useBlocks, block } from "@/lib/cms";
import { meta } from "@/lib/metaPixel";

const FAQS = [
  { q: "Will this make my content sound like AI?", a: "No. DMS uses your onboarding answers, your Dive Brain, and the Dive Intelligence Layer to produce outputs based on your business, audience and voice - not generic internet templates. The more detail you give it, the more human and specific the outputs become." },
  { q: "Is this only for dive centres?", a: "No. It works for dive centres, dive shops, freediving schools, technical diving operators, dive travel businesses, gear brands, instructors and creators." },
  { q: "Can instructors use this?", a: "Yes. The Basic plan is built specifically for solo instructors, recreational divers and freelance creators." },
  { q: "Do I need marketing experience?", a: "No. Every tool guides you through a short brief. The Dive Intelligence Layer applies professional marketing structure for you." },
  { q: "Does it post for me?", a: "No. DMS gives you strategic outputs to review, edit and use yourself. We deliberately do not auto-post - your judgement matters." },
  { q: "Does it create fake AI images or videos?", a: "No. We do not generate fake UGC, fake reels or AI images. Everything you produce here is text, structure, strategy and copy - for the real footage you film." },
  { q: "What happens after the free trial?", a: "Your Dive Brain is archived and safely stored, never deleted. You can upgrade any time to re-activate generation. Nothing you've created is lost." },
  { q: "What is the Dive Brain?", a: "Your personalised identity layer - niche, audience, goals, tone, positioning and content pillars - synthesised from onboarding. Every output references it." },
  { q: "What is the Dive Intelligence Layer?", a: "A pre-installed marketing doctrine built specifically for the diving industry: hook archetypes, caption structure, CTA framework, psychological triggers and reel engineering. It operates silently inside every tool." },
];

export default function Landing() {
  const [open, setOpen] = useState(0);
  const blocks = useBlocks();
  const heroImgDesktop = block(blocks, "landing.hero.image_desktop", HERO_DESKTOP);
  const heroImgMobile = block(blocks, "landing.hero.image_mobile", HERO_MOBILE);
  const founderImg = block(blocks, "landing.founder.image", FOUNDER_IMG);

  // Fire ViewContent once when the marketing site's main sales page mounts.
  // (PageView is handled globally by the router — this is the commercial view.)
  useEffect(() => {
    meta.viewContent("DMS Landing", { content_category: "marketing_site" });
  }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#F8FAFC] text-[#1F2937]">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F8FAFC]/85 border-b border-[#E2E8F0]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-2.5 shrink-0">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
              <span className="text-[#4FD1C5] font-heading font-bold text-xs sm:text-sm">D</span>
            </div>
            <div className="leading-none">
              <div className="font-heading font-bold text-[13px] sm:text-[15px] text-[#0A2342]">Dive Media Strategist™</div>
              <div className="hidden sm:block text-[10px] tracking-[0.22em] text-[#64748B] uppercase mt-1">For the diving industry</div>
            </div>
          </Link>
          <nav className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex dms-btn-ghost">Sign in</Link>
            <Link
              to="/register"
              data-testid="nav-register"
              onClick={() => meta.appStarted("nav_header", "/")}
              className="dms-btn-primary text-[13px] sm:text-[14px] px-3 sm:px-5 py-2 sm:py-2.5"
            >
              Get started <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero - mobile gets portrait image, desktop gets wide */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <picture>
            <source media="(min-width: 768px)" srcSet={heroImgDesktop} />
            <img src={heroImgMobile} alt="" className="w-full h-full object-cover opacity-30" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-[#F8FAFC] via-[#F8FAFC]/30 to-[#F8FAFC]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 pb-16 sm:pt-20 sm:pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="dms-badge mb-5 sm:mb-6" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4FD1C5]" />
              {block(blocks, "landing.hero.badge", "AI marketing operating system for divers")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-[1.05] text-[#0A2342]">
              {block(blocks, "landing.hero.title_top", "AI Marketing Tools")}<br />
              <span className="text-[#1D4E89]">{block(blocks, "landing.hero.title_bottom", "Built For The Diving Industry.")}</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-[16px] sm:text-[18px] leading-relaxed text-[#475569] max-w-2xl whitespace-pre-line">
              {block(blocks, "landing.hero.body", "Create reels, captions, emails, ads, blogs and full marketing campaigns - without sounding like generic AI. DMS builds a Dive Brain around your business, your audience, your offers and your voice.")}
            </p>
            <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                onClick={() => meta.appStarted("hero_primary", "/")}
                className="dms-btn-primary w-full sm:w-auto justify-center"
              >
                {block(blocks, "landing.hero.cta_primary", "Build my Dive Brain")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-cta-secondary" className="dms-btn-ghost w-full sm:w-auto justify-center">
                {block(blocks, "landing.hero.cta_secondary", "I already have an account")}
              </Link>
            </div>
            <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-2 sm:gap-6 text-xs sm:text-sm text-[#64748B]">
              <div className="flex items-center gap-2"><Compass className="w-4 h-4 text-[#4FD1C5]" /> Built for the niche</div>
              <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-[#4FD1C5]" /> Trained on DIL™</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#4FD1C5]" /> GDPR-ready</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative" data-testid="hero-reel-mosaic">
              <div className="absolute -inset-6 rounded-[28px] bg-[#4FD1C5]/10 blur-2xl" />
              <div className="relative dms-card p-2 sm:p-2.5 overflow-hidden">
                <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                  {[1, 2, 3, 4, 5, 6].map((n, i) => (
                    <div
                      key={n}
                      className="relative aspect-[3/4] rounded-md overflow-hidden bg-[#0A2342]/5 group animate-fade-up"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <img
                        src={`/hero-covers/cover${n}.jpg`}
                        alt="Diving reel that reached millions"
                        loading={i < 3 ? "eager" : "lazy"}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-2.5 sm:mt-3 flex items-baseline justify-between px-1.5 sm:px-2 pb-0.5">
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#1D4E89]">Live examples</div>
                  <div className="text-[11.5px] text-[#64748B]">Reels engineered with DMS · <span className="text-[#0A2342] font-bold">10M+</span> combined views</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objection */}
      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="dms-label mb-3">The objection</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342] leading-tight">
              &ldquo;I don&apos;t want my dive business to sound like AI.&rdquo;
            </h2>
          </div>
          <div className="lg:col-span-7 text-[16px] leading-relaxed text-[#475569] space-y-4">
            <p><strong className="text-[#0A2342]">Generic AI makes generic content.</strong> Dive Media Strategist is different.</p>
            <p>It builds a personalised Dive Brain around your business, your audience, your offers, and your voice. The more detail you give it during onboarding, the more specific, useful, and human the outputs become.</p>
            <p>This is not AI slop. This is diving-specific marketing intelligence, built by someone who actually works in the diving industry.</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
        <div className="max-w-3xl">
          <div className="dms-label mb-3">How it works</div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">Six steps from sign-up to a publish-ready piece.</h2>
        </div>
        <div className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {[
            { n: "01", t: "Complete onboarding", d: "Nine focused questions about your business, audience, goals and voice." },
            { n: "02", t: "Build your Dive Brain", d: "We synthesise your answers into your personal identity layer." },
            { n: "03", t: "Activate the DIL™", d: "Our private Dive Intelligence Layer applies pro marketing structure silently." },
            { n: "04", t: "Choose your tool", d: "Reels, carousels, emails, ads, blogs, course promos, gear promos - 12 tools." },
            { n: "05", t: "Generate outputs", d: "Personalised, specific, save-worthy content based on your brief." },
            { n: "06", t: "Save, export, publish", d: "Auto-saved to your Content Vault. Copy, download as TXT or DOCX." },
          ].map((s) => (
            <div key={s.n} className="dms-card p-6">
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Step {s.n}</div>
              <h3 className="mt-3 font-heading font-bold text-[18px] text-[#0A2342]">{s.t}</h3>
              <p className="mt-2 text-[14.5px] text-[#475569] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founder authority */}
      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[28px] bg-[#1D4E89]/10 blur-2xl" />
              <div className="relative dms-card p-1.5 overflow-hidden">
                <img src={founderImg} alt="Robin underwater" className="w-full h-[340px] sm:h-[460px] object-cover rounded-[10px]" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="dms-label mb-3">The founder</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">{block(blocks, "landing.founder.title", "Most marketing tools don't understand diving. I do.")}</h2>
            <div className="mt-5 sm:mt-6 text-[15.5px] sm:text-[16px] leading-relaxed text-[#475569] whitespace-pre-line">
              {block(blocks, "landing.founder.body", "I've spent my life between hospitality, diving, teaching, content creation, and digital marketing. With 3,000+ dives, scuba and freediving instructor experience, technical diving training, and hands-on work with dive centres, shops, and underwater brands - I know what actually makes divers trust, click, book, and buy.\n\nI built Dive Media Strategist to give dive professionals a simple AI-powered content system that speaks their language. No generic prompts. No random posting. No \"just make reels\" nonsense.\n\nJust clear strategy, stronger hooks, better captions, and content that helps turn real dive experience into authority, leads and sales.")}
            </div>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: Award, label: "PADI Staff Instructor" },
                { icon: Anchor, label: "Freedive Instructor" },
                { icon: Sparkles, label: "Technical Diver" },
                { icon: Camera, label: "Content creator" },
                { icon: Code, label: "AI systems builder" },
                { icon: Compass, label: "3,000+ dives" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-[13px] text-[#1F2937]">
                  <Icon className="w-4 h-4 text-[#4FD1C5]" />
                  <span className="font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tool showcase */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
        <div className="max-w-3xl">
          <div className="dms-label mb-3">17 tools, one operating system</div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">Every output engineered against the same doctrine.</h2>
          <p className="mt-3 text-[#475569]">Pick a tool. Brief in plain English. Get a publish-ready, on-brand output in your voice.</p>
        </div>
        <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {TOOLS.map((t) => (
            <div key={t.key} className="dms-card dms-card-hover p-4 sm:p-5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">{t.tier}</span>
              </div>
              <h3 className="mt-2 font-heading font-bold text-[14.5px] sm:text-[15px] text-[#0A2342] leading-snug">{t.title}</h3>
              <p className="mt-1 text-[12.5px] sm:text-[13px] text-[#64748B] leading-relaxed">{t.short}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
        <div className="dms-label mb-3">FAQ</div>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">Answers before you start.</h2>
        <div className="mt-8 sm:mt-10 dms-card divide-y divide-[#E2E8F0]">
          {FAQS.map((f, i) => (
            <details key={i} open={open === i} className="group" onToggle={(e) => e.target.open && setOpen(i)}>
              <summary data-testid={`faq-${i}`} className="cursor-pointer list-none p-5 sm:p-6 flex items-start gap-4">
                <div className="flex-1 font-heading font-bold text-[15.5px] sm:text-[16px] text-[#0A2342]">{f.q}</div>
                <ChevronDown className="w-4 h-4 mt-1 text-[#64748B] transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1 text-[14.5px] text-[#475569] leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pb-16 sm:pb-24">
        <div className="dms-card p-7 sm:p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#4FD1C5]/15 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Your feed is leaking reach. Plug it.</h2>
              <p className="mt-3 sm:mt-4 text-[#475569] leading-relaxed max-w-2xl">
                7-day free Pro trial. No card required up front. Cancel any time.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/register"
                data-testid="bottom-cta"
                onClick={() => meta.appStarted("bottom_cta", "/")}
                className="dms-btn-accent w-full lg:w-auto justify-center"
              >
                Start free <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E8F0] py-8 text-center text-[11px] sm:text-xs tracking-[0.18em] text-[#64748B] uppercase">
        <div>© Dive Media Strategist™ - Engineered for divers who create.</div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-3 sm:gap-5 normal-case tracking-normal">
          <Link to="/legal/terms" className="hover:text-[#0A2342]">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-[#0A2342]">Privacy</Link>
          <Link to="/legal/disclaimer" className="hover:text-[#0A2342]">Disclaimer</Link>
          <Link to="/trust" className="hover:text-[#0A2342]" data-testid="footer-trust-link">Trust</Link>
          <Link to="/consulting" className="hover:text-[#0A2342]">Consulting</Link>
          <Link to="/support" className="hover:text-[#0A2342]">Support</Link>
        </div>
      </footer>
    </div>
  );
}
